import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { AiAssistantButton } from '@client/components/_shared/ai/AiAssistantButton';
import { EntityDetailsHeader } from '@client/components/_ui/data-display/EntityDetailsHeader';
import {
    ENTITY_HEADER_FLAGS_COLUMN_CLASS,
    EntityHeaderFlags,
} from '@client/components/_ui/data-display/EntityHeaderFlags';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { ClaimClientUpdateIndicator } from '@client/components/claims/ClaimClientUpdateIndicator';
import { ClaimFlagManage } from '@client/components/claims/ClaimFlagManage';
import { ClaimFlagTag } from '@client/components/claims/ClaimFlagTag';
import { ClaimStatesBar } from '@client/components/claims/ClaimStatesBar';
import { ClaimStatusTag } from '@client/components/claims/ClaimStatusTag';
import { NoteEdit } from '@client/components/claims/NoteEdit';
import {
    FastTrackChip,
    FraudWatchChip,
    ProceedChip,
    ReadinessChip,
    UrgencyChip,
} from '@client/components/claims/unified-assessment/chips';
import { ComplaintForm } from '@client/components/complaints/complaint-form';
import { ContactPopover } from '@client/components/contacts/ContactPopover';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useClaimStatesCapability } from '@client/hooks/business/claim/useClaimStatesCapability';
import { useConfigServiceLoader } from '@client/hooks/business/configuration/useConfigServiceLoader';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useCustomDocumentTitle } from '@client/hooks/ui/useCustomDocumentTitle';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { ICONS } from '@client/styles/theme';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { cn } from '@client/utils';
import { useContentReadyMark } from '@client/utils/performance/content-ready';
import { DISPLAY_COLOR_CONFIGURATION } from '@client/utils/ui/display-configuration';
import { isVehicleProductCategory } from '@shared/constants';
import { hasClaimUpdateAccess } from '@shared/permissions/claim';
import { hasPermission } from '@shared/permissions/roles';
import type { ClaimFlagType } from '@shared/prisma/enums';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { Tabs as AntTabs, Badge, Divider, Drawer, Form, Skeleton } from 'antd';
import { Decimal } from 'decimal.js';
import { Car, Flag, Mail, MessageCircle, NotebookPen, Phone, Siren } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { match } from 'ts-pattern';
import { claimFormStore, useInitClaimForm } from './store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id')({
    staticData: {
        breadcrumb: {
            title: 'Claim details',
        },
        ui: {
            widthVariant: 'default',
        },
        entity: 'claim',
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClaimDetails />
        </PageController>
    );
}

const zeroDecimal = new Decimal(0);

function HeaderIncurredCost({
    isLoading,
    reserve: reserveProp,
    recovery: recoveryProp,
    excess: excessProp,
}: {
    isLoading: boolean;
    reserve?: Decimal;
    recovery?: Decimal;
    excess?: Decimal;
}) {
    const { formatCurrency } = useFormatter();

    const [type, setType] = useState<'net' | 'total'>('total');

    const reserve = reserveProp || zeroDecimal;
    const recovery = recoveryProp || zeroDecimal;
    const excess = excessProp || zeroDecimal;

    return (
        <div className="flex flex-col gap-0.5 rounded-lg border border-separator bg-card px-3 py-2">
            <span className="text-muted-foreground text-xs">Costs incurred</span>
            <div className="flex items-center gap-2">
                <span className="font-medium text-lg">
                    {isLoading ? (
                        <Skeleton.Node
                            active
                            classNames={{
                                content: 'w-20 h-7 mb-0.5',
                            }}
                        />
                    ) : (
                        formatCurrency(type === 'net' ? Decimal.max(0, reserve.minus(recovery).minus(excess)) : reserve)
                    )}
                </span>
                <SegmentedControl
                    value={type}
                    onChange={setType}
                    size="small"
                    options={[
                        {
                            label: 'Total',
                            value: 'total',
                            tooltip: 'Show total cost incurred for the claim. This is the reserve amount.',
                        },
                        {
                            label: 'Net',
                            value: 'net',
                            tooltip:
                                'Show net cost incurred for the claim. This is the total cost incurred minus the recovery and excess amounts.',
                        },
                    ]}
                />
            </div>
        </div>
    );
}

function Header() {
    const claim = claimFormStore.use.formData();
    const enableHeaderTriageChips = useFeatureFlag('enable-header-triage-chips');
    const enableAiTriage = useFeatureFlag('enable-ai-triage', false);
    const getTriageQuery = trpc.claim.aiTriage.getTriage.useQuery(
        claim.id && enableHeaderTriageChips && enableAiTriage ? { claimId: claim.id } : skipToken,
    );
    const getFinancialSummaryQuery = trpc.claim.getFinancialSummary.useQuery({ claimId: claim.id });
    const clientUpdateConfigQuery = useConfigServiceLoader(async (configService) =>
        configService.fetchClaimClientUpdateConfiguration({
            staleTime: 'immediate',
        }),
    );
    const { formatDate } = useFormatter();

    const triageAssessment = getTriageQuery.data?.assessment;

    const policyHolderContact = claim.Contacts?.find(({ type }) => type === 'POLICY_HOLDER');
    const insurerContact = claim.Contacts?.find(({ type }) => type === 'INSURER');
    const brokerContact = claim.Contacts?.find(({ type }) => type === 'BROKER');

    const primaryClaimFlagRow = claim.ClaimFlags.find((item) => item.type === 'PRIMARY');
    const primaryClaimFlag = primaryClaimFlagRow
        ? {
              name: primaryClaimFlagRow.ClaimFlag.name,
              displayColor: primaryClaimFlagRow.ClaimFlag.displayColor,
              isSystemDefined: primaryClaimFlagRow.ClaimFlag.isSystemDefined,
          }
        : undefined;
    const secondaryClaimFlags =
        claim.ClaimFlags.filter((item) => item.type === 'SECONDARY').map((item) => ({
            name: item.ClaimFlag.name,
            type: item.type,
            displayColor: item.ClaimFlag.displayColor,
            isSystemDefined: item.ClaimFlag.isSystemDefined,
        })) || [];
    const labelClaimFlags =
        claim.ClaimFlags.filter((item) => item.type === 'LABEL').map((item) => ({
            name: item.ClaimFlag.name,
            type: item.type as Extract<ClaimFlagType, 'LABEL'>,
            isSystemDefined: item.ClaimFlag.isSystemDefined,
        })) || [];
    const flagConfiguration = primaryClaimFlag ? DISPLAY_COLOR_CONFIGURATION[primaryClaimFlag.displayColor] : null;

    return (
        <TRPCErrorBoundary errors={[getTriageQuery.error, getFinancialSummaryQuery.error]}>
            <EntityDetailsHeader
                classNames={
                    flagConfiguration
                        ? {
                              card: cn(
                                  flagConfiguration.filled.borderColorClass,
                                  flagConfiguration.outlined.backgroundColorClass,
                              ),
                          }
                        : undefined
                }
                accentClassName={flagConfiguration?.filled.borderColorClass}
                title={claim.generatedId || String(claim.id) || '-'}
                titleAddon={
                    <>
                        <ClaimStatusTag variant="outlined" data={claim.Status} />
                        {claim.createdAt && (
                            <span className="text-muted-foreground text-xs">Lodged {formatDate(claim.createdAt)}</span>
                        )}
                        {claim.claimOccurredAt && (
                            <span className="text-muted-foreground text-xs">
                                Loss {formatDate(claim.claimOccurredAt)}
                            </span>
                        )}
                    </>
                }
                mainInfo={[
                    {
                        title: 'Product type',
                        children: claim.ProductType?.name || '-',
                    },
                    {
                        title: 'Policy holder',
                        children: claim.phCompany || policyHolderContact?.fullName || '-',
                        ...(policyHolderContact?.id && {
                            className: 'cursor-pointer transition hover:opacity-70',
                            renderWrapper({ children }) {
                                return <ContactPopover id={policyHolderContact.id}>{children}</ContactPopover>;
                            },
                        }),
                    },
                    {
                        title: 'Insurer',
                        children: claim.Insurer?.name || '-',
                        ...(insurerContact?.id && {
                            className: 'cursor-pointer transition hover:opacity-70',
                            renderWrapper({ children }) {
                                return <ContactPopover id={insurerContact.id}>{children}</ContactPopover>;
                            },
                        }),
                    },
                    {
                        title: 'Broker',
                        children: claim.brokerCompany || '-',
                        ...(brokerContact?.id && {
                            className: 'cursor-pointer transition hover:opacity-70',
                            renderWrapper({ children }) {
                                return <ContactPopover id={brokerContact.id}>{children}</ContactPopover>;
                            },
                        }),
                    },
                    ...(claim.ClaimFlags.length
                        ? [
                              {
                                  title: 'Flags',
                                  className: ENTITY_HEADER_FLAGS_COLUMN_CLASS,
                                  children: (
                                      <EntityHeaderFlags
                                          primary={
                                              primaryClaimFlag && (
                                                  <ClaimFlagTag data={{ ...primaryClaimFlag, type: 'PRIMARY' }} />
                                              )
                                          }
                                          secondary={secondaryClaimFlags.map((item) => (
                                              <ClaimFlagTag key={item.name} data={item} />
                                          ))}
                                          labels={labelClaimFlags.map((item) => (
                                              <ClaimFlagTag key={item.name} data={item} />
                                          ))}
                                      />
                                  ),
                              },
                          ]
                        : []),
                ]}
                rightAddon={
                    <>
                        <HeaderIncurredCost
                            isLoading={getFinancialSummaryQuery.isLoading}
                            reserve={getFinancialSummaryQuery.data?.totalReserveAmount}
                            recovery={getFinancialSummaryQuery.data?.recoveryAmount}
                            excess={getFinancialSummaryQuery.data?.excessAmount}
                        />
                        <ClaimClientUpdateIndicator
                            data={{
                                claimId: claim.id,
                                Status: claim.Status,
                            }}
                            label={clientUpdateConfigQuery.data?.headerWidgetLabel || undefined}
                        />
                    </>
                }
                bottomAddon={
                    enableHeaderTriageChips &&
                    triageAssessment && (
                        <div className="flex flex-wrap items-center gap-1">
                            <UrgencyChip
                                level={triageAssessment.urgency.level}
                                reasoning={triageAssessment.urgency.reasoning}
                            />
                            <ReadinessChip
                                status={triageAssessment.triageReadiness.status}
                                reasoning={triageAssessment.triageReadiness.reasoning}
                                missingFields={triageAssessment.triageReadiness.missingFields}
                            />
                            <ProceedChip
                                status={triageAssessment.proceedRecommendation.status}
                                reasoning={triageAssessment.proceedRecommendation.reasoning}
                            />
                            <FastTrackChip
                                suitable={triageAssessment.fastTrackSuitability.suitable}
                                reasoning={triageAssessment.fastTrackSuitability.reasoning}
                                blockers={triageAssessment.fastTrackSuitability.blockers}
                            />
                            <FraudWatchChip
                                level={triageAssessment.fraudWatch.level}
                                reasoning={triageAssessment.fraudWatch.reasoning}
                                signals={triageAssessment.fraudWatch.signals}
                            />
                        </div>
                    )
                }
            />
        </TRPCErrorBoundary>
    );
}

function ComplaintAdd({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const claim = claimFormStore.use.formData();
    const { trpcUtils } = useTrpc();

    const [form] = Form.useForm();

    return (
        <Drawer
            title="New complaint"
            size={900}
            open={isOpen}
            onClose={() => {
                form.resetFields();
                onClose();
            }}
            destroyOnHidden
        >
            <ComplaintForm
                claimId={claim.id}
                formOverride={form}
                onCancel={onClose}
                onAfterSaveCancel={() => {
                    void trpcUtils.claim.complaint.listClaimComplaints.invalidate();
                    onClose();
                }}
            />
        </Drawer>
    );
}

function Actions() {
    const orgId = useOrgId();
    const claim = claimFormStore.use.formData();
    const isReadOnly = claimFormStore.use.isReadonly();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const user = useUser();
    // complaint.createComplaint enforces `complaint:create`, so without it this button only leads to
    // a filled-in form and a FORBIDDEN on save.
    const canAddComplaint =
        useOrg().enabledModules.includes('compliance') && hasPermission(user.roles, 'complaint:create');

    const [isNewNoteDrawerOpen, setIsNewNoteDrawerOpen] = useState(false);
    const [isNewComplaintDrawerOpen, setIsNewComplaintDrawerOpen] = useState(false);
    const [isFlagsDrawerOpen, setIsFlagsDrawerOpen] = useState(false);

    return (
        <>
            <NoteEdit
                entityId={claim.id}
                entity="claim"
                open={isNewNoteDrawerOpen && !isReadOnly}
                type="NOTE"
                title="New note"
                onClose={() => {
                    setIsNewNoteDrawerOpen(false);
                    void trpcUtils.communication.listCommLogAndNotes.invalidate();
                    navigate({
                        to: '/console/$orgId/claims/details/$id/activity',
                        params: {
                            orgId,
                            id: claim.id,
                        },
                    });
                }}
            />
            {canAddComplaint && (
                <ComplaintAdd isOpen={isNewComplaintDrawerOpen} onClose={() => setIsNewComplaintDrawerOpen(false)} />
            )}
            <ClaimFlagManage
                isOpen={isFlagsDrawerOpen}
                claimId={claim.id}
                isReadOnly={isReadOnly}
                onClose={() => setIsFlagsDrawerOpen(false)}
            />

            <div className="mb-4 flex items-center gap-2">
                {/* CUR-725: quick actions that create records are withheld on a closed
                    (read-only) claim; the flags drawer stays viewable (it disables its save). */}
                {!isReadOnly && (
                    <>
                        <Button.Icon.Link
                            icon={<Mail />}
                            color="blue"
                            bordered
                            tooltip="Send email"
                            to="/console/$orgId/claims/details/$id/comms/add/email"
                            params={{
                                orgId,
                                id: claim.id,
                            }}
                        />
                        <Button.Icon.Link
                            icon={<MessageCircle />}
                            color="green"
                            bordered
                            tooltip="Send SMS"
                            to="/console/$orgId/claims/details/$id/comms/add/sms"
                            params={{
                                orgId,
                                id: claim.id,
                            }}
                        />
                        <Button.Icon.Link
                            icon={<Phone />}
                            color="purple"
                            bordered
                            tooltip="Log call"
                            to="/console/$orgId/claims/details/$id/activity/add/phone"
                            params={{
                                orgId,
                                id: claim.id,
                            }}
                        />
                        <Button.Icon
                            icon={<NotebookPen />}
                            color="gold"
                            bordered
                            tooltip="Add note"
                            aria-label="Add note"
                            onClick={() => setIsNewNoteDrawerOpen(true)}
                        />
                    </>
                )}
                <Button.Icon
                    icon={<Flag />}
                    color="red"
                    bordered
                    tooltip="Manage flags"
                    onClick={() => setIsFlagsDrawerOpen(true)}
                />

                {canAddComplaint && (
                    <>
                        <Divider orientation="vertical" className="h-8" />

                        <Button
                            type="secondary"
                            icon={<Siren />}
                            className="py-0.5!"
                            onClick={() => setIsNewComplaintDrawerOpen(true)}
                        >
                            New complaint
                        </Button>
                    </>
                )}

                <div className="flex grow justify-end">
                    <AiAssistantButton
                        entityType="claim"
                        entityId={claim.id}
                        entityName="Claim"
                        outputFormat="markdown"
                    />
                </div>
            </div>
        </>
    );
}

function ClaimDetails() {
    const orgId = useOrgId();
    const currentOrg = useOrg();
    const enableUnifiedAiAssessment = useFeatureFlag('enable-ai-unified-assessment');
    const { isEnabled: enableClaimStates } = useClaimStatesCapability();
    const { id } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const claimQuery = trpc.claim.getClaim.useQuery(id ? { id } : skipToken, {
        throwOnError(error) {
            message.destroy();

            match(error.data?.errorId)
                .with('recordNotFound', () => {
                    void errorMessage
                        .showAsync(
                            "The claim you are trying to access either doesn't exist or you don't have permissions to access it.",
                        )
                        .then(() => {
                            navigate({
                                to: '/console/$orgId/claims',
                                params: { orgId },
                            });
                        });
                })
                .otherwise(() => {
                    errorMessage.show(error);
                });

            return false;
        },
    });
    const user = useUser();
    const { mutateAsync } = trpc.claim.updateClaim.useMutation();

    const [input] = useState<RouterInputs['notification']['getUnreadNotificationCount']>({
        where: {
            isActive: true,
            CommLog: {
                claimId: id,
            },
        },
    });

    const { data: unreadNotificationCount } = trpc.notification.getUnreadNotificationCount.useQuery(input, {
        refetchInterval: 1000 * 60,
    });

    const { formData: claim } = useInitClaimForm({
        data: claimQuery.data,
        isLoading: claimQuery.isLoading,

        async onReload() {
            await claimQuery.refetch();
        },

        async onSave(_, changedValues) {
            try {
                await mutateAsync({
                    id,
                    set: changedValues,
                });

                await trpcUtils.claim.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    useCustomDocumentTitle(`Claim - ${claim?.generatedId || id} - Curium`);

    const { pathname } = useLocation();

    // LCP guardrail (CUR-1036, task 0.1): the page's content is ready once the current
    // claim record has resolved — the static chrome below painting earlier must not
    // move this.
    useContentReadyMark(Boolean(claim && id === claim.id));

    const isProductTypeCustomFieldsDataExists =
        claim?.ProductType?.definition?.cards?.some((card) => card.type === 'FORM') ||
        Object.values(claim?.productTypeCustomFieldsData || {}).length;

    return claim && id === claim.id ? (
        <>
            <Header />
            {enableClaimStates && (
                <ClaimStatesBar
                    claimId={claim.id}
                    statesSnapshot={claim.statesSnapshot}
                    currentStatePosition={claim.currentStatePosition}
                    statesCompletedAt={claim.statesCompletedAt}
                    isClaimClosed={claim.Status.type === 'CLOSED'}
                    canAdvance={hasClaimUpdateAccess(user, claim)}
                />
            )}
            <Actions />
            <Tabs
                items={[
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.manage size={16} />
                                <span>Manage claim</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/manage' as const,
                    },
                    ...(enableUnifiedAiAssessment
                        ? [
                              {
                                  label: (
                                      <span className="flex items-center gap-2">
                                          <ICONS.ai size={16} />
                                          <span>AI assessment</span>
                                      </span>
                                  ),
                                  to: '/console/$orgId/claims/details/$id/ai-assessment' as const,
                              },
                          ]
                        : []),
                    // One tab for the whole vehicle workstream — the AI damage scan (Ravin AI)
                    // and the repair assessment (Clear21) are sections within it. Shown only for
                    // vehicle product types; a product type merely *named* "Motor" does not
                    // qualify until its category is set in Configuration → Product types.
                    ...(isVehicleProductCategory(claim.ProductType?.category)
                        ? [
                              {
                                  label: (
                                      <span className="flex items-center gap-2">
                                          <Car size={16} />
                                          <span>Vehicle</span>
                                      </span>
                                  ),
                                  to: '/console/$orgId/claims/details/$id/vehicle' as const,
                              },
                          ]
                        : []),
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.policyInfo size={16} />
                                <span>Policy info</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/policy-info' as const,
                    },
                    ...(isProductTypeCustomFieldsDataExists
                        ? [
                              {
                                  label: (
                                      <span className="flex items-center gap-2">
                                          <ICONS.claim size={16} />
                                          <span>
                                              {claim.ProductType?.definition?.claimTabLabel || 'Product type data'}
                                          </span>
                                      </span>
                                  ),
                                  to: '/console/$orgId/claims/details/$id/product-type-data' as const,
                              },
                          ]
                        : []),
                    {
                        label: (
                            <span className="relative flex items-center gap-2">
                                <ICONS.communication size={16} />
                                <span>Communication</span>
                                {(unreadNotificationCount?.count || 0) > 0 && (
                                    <Badge
                                        count={unreadNotificationCount?.count || 0}
                                        size="small"
                                        className="absolute -top-2 -right-4"
                                    />
                                )}
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/comms' as const,
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.activity size={16} />
                                <span>Activity</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/activity' as const,
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.contacts size={16} />
                                <span>Contacts</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/contacts' as const,
                    },
                    ...(currentOrg.enabledModules.includes('billing')
                        ? [
                              {
                                  label: (
                                      <span className="flex items-center gap-2">
                                          <ICONS.expenses size={16} />
                                          <span>Expenses and billing</span>
                                      </span>
                                  ),
                                  to: '/console/$orgId/claims/details/$id/expenses' as const,
                              },
                          ]
                        : []),
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.financials size={16} />
                                <span>Financials</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/financials' as const,
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.library size={16} />
                                <span>Library</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/library' as const,
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.history size={16} />
                                <span>History</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/history' as const,
                    },
                ]}
            />
        </>
    ) : (
        // Static chrome before data (CUR-1036, task 2.5). The real `Tabs` wrapper above
        // mounts the child route via `<Outlet />`, and the manage tab reads the claim
        // form store the record populates — so pre-data this renders a visual-only strip
        // (plain antd Tabs, inert clicks) of the record-INDEPENDENT tabs in their final
        // position, a dimension-reserving header skeleton, and an empty spacer where the
        // action row lands. Record-dependent tabs (Vehicle, product-type data) and the
        // states bar appear once the record resolves. Keep these labels in sync with the
        // real items above.
        <>
            <Skeleton active title paragraph={{ rows: 1 }} className="mb-4" />
            <div className="mb-4 flex h-8 items-center gap-2" />
            <div role="region" aria-busy="true" aria-label="Loading claim details">
                <AntTabs
                    activeKey={
                        STATIC_CLAIM_TAB_KEYS.includes(pathname.split('/')[6] ?? '') ? pathname.split('/')[6] : 'manage'
                    }
                    items={[
                        staticClaimTab('manage', <ICONS.manage size={16} />, 'Manage claim'),
                        ...(enableUnifiedAiAssessment
                            ? [staticClaimTab('ai-assessment', <ICONS.ai size={16} />, 'AI assessment')]
                            : []),
                        staticClaimTab('policy-info', <ICONS.policyInfo size={16} />, 'Policy info'),
                        staticClaimTab('comms', <ICONS.communication size={16} />, 'Communication'),
                        staticClaimTab('activity', <ICONS.activity size={16} />, 'Activity'),
                        staticClaimTab('contacts', <ICONS.contacts size={16} />, 'Contacts'),
                        ...(currentOrg.enabledModules.includes('billing')
                            ? [staticClaimTab('expenses', <ICONS.expenses size={16} />, 'Expenses and billing')]
                            : []),
                        staticClaimTab('financials', <ICONS.financials size={16} />, 'Financials'),
                        staticClaimTab('library', <ICONS.library size={16} />, 'Library'),
                        staticClaimTab('history', <ICONS.history size={16} />, 'History'),
                    ]}
                />
            </div>
        </>
    );
}

/** Path segments of the record-independent claim tabs (pre-data strip highlight only). */
const STATIC_CLAIM_TAB_KEYS = [
    'manage',
    'ai-assessment',
    'policy-info',
    'comms',
    'activity',
    'contacts',
    'expenses',
    'financials',
    'library',
    'history',
];

function staticClaimTab(key: string, icon: ReactNode, text: string) {
    return {
        key,
        disabled: true,
        label: (
            <span className="flex items-center gap-2">
                {icon}
                <span>{text}</span>
            </span>
        ),
        children: <Skeleton active className="mt-4" />,
    };
}
